import React, { useMemo, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import {
  CardComp,
  ButtonComp,
  ModalCompAdvanceUpdate,
} from "../../../components";
import { TableCompData } from "../../../components/Table";
import TablePagination from "../../../components/Table/Pagination";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import EmptyTable from "../../../components/Common/EmptyTable";
import {
  formatMoney,
  convertToTitleCase,
  formatAmountInputDisplay,
  parseAmountInputToNumber,
} from "../../../utils";
import {
  useGetBranchWalletBalanceQuery,
  useGetBranchWalletWithdrawalsQuery,
  useGetBranchWalletLedgerQuery,
  useGetBranchWalletBankAccountsQuery,
  useRequestBranchWalletWithdrawalMutation,
} from "../../../store/admin/branchWalletSlice";
import { formatPaymentCategory } from "./branchWalletDetailHelpers";

function DetailRow({ label, value }) {
  return (
    <div className="d-flex justify-content-between align-items-start gap-3 py-2 border-bottom border-light">
      <span className="text-muted small mb-0" style={{ minWidth: "140px" }}>
        {label}
      </span>
      <div className="text-end fw-semibold text-black2 small mb-0" style={{ maxWidth: "72%" }}>
        {value}
      </div>
    </div>
  );
}

function withdrawalStatusStyle(status) {
  const s = String(status || "").toUpperCase();
  if (s === "PENDING") return { backgroundColor: "#ff9500" };
  if (s === "PROCESSING") return { backgroundColor: "var(--color-secondary)" };
  if (s === "PAID") return { backgroundColor: "#19bc31" };
  if (s === "FAILED" || s === "REJECTED") return { backgroundColor: "#ff4343" };
  return { backgroundColor: "#A1A0A0" };
}

function formatStaffDisplayName(staff) {
  if (!staff || typeof staff !== "object") return "—";
  const parts = [staff.first_name, staff.last_name, staff.other_name].filter(
    (p) => p && String(p).trim()
  );
  const joined = parts.join(" ").trim();
  if (joined) return joined;
  if (staff.email) return String(staff.email);
  return "—";
}

const WALLET_TABS = {
  WITHDRAWALS: "withdrawals",
  LEDGER: "ledger",
};

export default function BranchSettlementWallet() {
  const [page, setPage] = useState(1);
  const [wdPage, setWdPage] = useState(1);
  const [walletTab, setWalletTab] = useState(WALLET_TABS.LEDGER);
  const [modalPage, setModalPage] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [selectedBankId, setSelectedBankId] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedWithdrawalRequest, setSelectedWithdrawalRequest] = useState(null);

  const { data: balanceData, isFetching: isBalanceLoading } =
    useGetBranchWalletBalanceQuery();
  const { data: wdListData, isFetching: isWdListLoading } = useGetBranchWalletWithdrawalsQuery(
    { page: wdPage, per_page: 20 },
    { skip: walletTab !== WALLET_TABS.WITHDRAWALS, refetchOnMountOrArgChange: true }
  );
  const { data: ledgerData, isFetching: isLedgerLoading } = useGetBranchWalletLedgerQuery(
    { page, per_page: 20 },
    { skip: walletTab !== WALLET_TABS.LEDGER }
  );
  const { data: banks = [], isFetching: isBanksLoading } =
    useGetBranchWalletBankAccountsQuery();
  const [requestWithdrawal, { isLoading: isSubmitting }] =
    useRequestBranchWalletWithdrawalMutation();

  const balanceNgn = balanceData?.balance_ngn ?? 0;
  const pendingRecoveryNgn = balanceData?.pending_recovery_ngn ?? 0;
  const pendingWithdrawalsTotalNgn = balanceData?.pending_withdrawals_total_ngn ?? 0;
  const availableToWithdrawNgn = balanceData?.available_to_withdraw_ngn ?? 0;

  const wdListRows = wdListData?.rows ?? [];
  const wdPagination = wdListData?.pagination ?? {};
  const withdrawalTableData = useMemo(() => {
    return wdListRows.map((row, idx) => ({
      ...row,
      serialNumber: idx + 1 + (wdPage - 1) * (wdPagination?.perPage || 20),
    }));
  }, [wdListRows, wdPage, wdPagination?.perPage]);

  const withdrawalColumns = useMemo(
    () => [
      {
        name: "S/N",
        width: "64px",
        selector: (row) => row.serialNumber,
      },
      {
        name: "Amount",
        minWidth: "120px",
        selector: (row) => formatMoney(Number(row?.amount_ngn ?? 0).toFixed(2), true),
      },
      {
        name: "Status",
        minWidth: "120px",
        cell: (row) => (
          <span
            className="rounded-pill px-2 py-1 text-white small"
            style={withdrawalStatusStyle(row?.status)}
          >
            {convertToTitleCase(String(row?.status || "—").toLowerCase())}
          </span>
        ),
      },
      {
        name: "Settlement account",
        minWidth: "180px",
        cell: (row) => {
          const b = row?.company_bank;
          if (!b) return "—";
          const full = [b.bank_name, b.account_number, b.account_name].filter(Boolean).join(" · ");
          const short = [b.bank_name, b.account_number].filter(Boolean).join(" · ");
          return (
            <span className="d-inline-block text-truncate" style={{ maxWidth: "220px" }} title={full}>
              {short || "—"}
            </span>
          );
        },
      },
      {
        name: "Requested",
        minWidth: "130px",
        selector: (row) =>
          row?.created_at ? moment(row.created_at).format("DD-MMM-YYYY HH:mm") : "—",
      },
      {
        name: "Actions",
        width: "100px",
        center: true,
        cell: (row) => (
          <ButtonComp
            btnText="View"
            btnClassName="border border-primary1 text-primary1 px-3 py-1 rounded bg-white"
            ButtonInlineStyle={{ whiteSpace: "nowrap", minWidth: "fit-content", fontSize: "13px" }}
            onClick={() => {
              setSelectedWithdrawalRequest(row);
              setModalPage("Withdrawal details");
            }}
          />
        ),
      },
    ],
    []
  );

  const tableData = useMemo(() => {
    const rows = ledgerData?.rows ?? [];
    return rows.map((row, idx) => ({
      ...row,
      serialNumber:
        idx + 1 + (page - 1) * (ledgerData?.pagination?.perPage || 20),
    }));
  }, [ledgerData, page]);

  const pagination = ledgerData?.pagination ?? {};

  const columns = useMemo(
    () => [
      {
        name: "S/N",
        width: "72px",
        selector: (row) => row.serialNumber,
      },
      {
        name: "Date",
        minWidth: "120px",
        selector: (row) =>
          row?.created_at
            ? moment(row.created_at).format("DD-MMM-YYYY")
            : "—",
      },
      {
        name: "Type",
        minWidth: "160px",
        selector: (row) => convertToTitleCase(String(row?.entry_type || "").replace(/_/g, " ")),
      },
      {
        name: "Direction",
        width: "100px",
        selector: (row) => convertToTitleCase(row?.direction || ""),
      },
      {
        name: "Amount",
        minWidth: "120px",
        selector: (row) => {
          const n = Math.abs(Number(row?.amount_ngn ?? 0));
          const isDebit = String(row?.direction || "").toLowerCase() === "debit";
          const formatted = formatMoney(n.toFixed(2), true);
          return isDebit ? `-${formatted}` : formatted;
        },
      },
      {
        name: "Reference",
        minWidth: "120px",
        selector: (row) => {
          const ref = row?.payment_reference || "—";
          if (ref.length <= 18) return ref;
          return `${ref.slice(0, 16)}…`;
        },
      },
      {
        name: "Actions",
        width: "110px",
        center: true,
        cell: (row) => (
          <ButtonComp
            btnText="View"
            btnClassName="border border-primary1 text-primary1 px-3 py-1 rounded bg-white"
            ButtonInlineStyle={{ whiteSpace: "nowrap", minWidth: "fit-content", fontSize: "13px" }}
            onClick={() => {
              setSelectedEntry(row);
              setModalPage("Transaction details");
            }}
          />
        ),
      },
    ],
    []
  );

  const handleSubmitWithdrawal = async () => {
    const amt = parseAmountInputToNumber(amountInput);
    const bankId = parseInt(selectedBankId, 10);
    if (!bankId) {
      toast.error("Select a settlement bank account.");
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    if (amt > availableToWithdrawNgn + 0.01) {
      toast.error(
        "Amount exceeds what you can withdraw while other requests are awaiting approval. Reduce the amount or wait for those requests to complete."
      );
      return;
    }
    try {
      const res = await requestWithdrawal({
        amount_ngn: amt,
        company_bank_id: bankId,
      }).unwrap();
      if (res?.status !== false) {
        toast.success(res?.message || "Withdrawal request submitted.");
        setModalPage("");
        setAmountInput("");
        setSelectedBankId("");
      } else {
        toast.error(res?.message || "Request failed.");
      }
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Could not submit withdrawal request."
      );
    }
  };

  const transactionDetailModal = (() => {
    const row = selectedEntry;
    if (!row) {
      return <p className="text-muted small mb-0">No row selected.</p>;
    }
    const meta = row.metadata && typeof row.metadata === "object" ? row.metadata : {};
    const entryType = String(row.entry_type || "").toUpperCase();

    if (entryType === "PAYMENT_SETTLEMENT") {
      const totalPaid =
        meta.gross_charged_ngn != null
          ? Number(meta.gross_charged_ngn)
          : meta.member_payable_total_ngn != null
            ? Number(meta.member_payable_total_ngn)
            : null;

      return (
        <div className="py-1">
          <p className="small text-muted mb-3">
            Summary only. Full fee breakdown is available to Uniquecoop platform administrators.
          </p>
          <DetailRow
            label="Payment reference"
            value={<span className="font-monospace text-break small">{row.payment_reference || "—"}</span>}
          />
          <DetailRow label="Category" value={formatPaymentCategory(meta.payment_category)} />
          <DetailRow
            label="Total paid by member"
            value={
              totalPaid != null && Number.isFinite(totalPaid)
                ? formatMoney(totalPaid.toFixed(2), true)
                : "—"
            }
          />
          <DetailRow
            label="Principal (loan / contribution)"
            value={
              meta.principal_ngn != null
                ? formatMoney(Number(meta.principal_ngn).toFixed(2), true)
                : "—"
            }
          />
          <DetailRow
            label="Amount credited to this branch wallet"
            value={
              <span className="text-primary1 fw-semibold">
                {formatMoney(Number(row.amount_ngn ?? 0).toFixed(2), true)}
              </span>
            }
          />
        </div>
      );
    }

    if (entryType === "WITHDRAWAL") {
      const payout =
        meta.payout_amount_ngn != null
          ? Number(meta.payout_amount_ngn)
          : Number(row.amount_ngn ?? 0);
      return (
        <div className="py-1">
          <p className="small text-muted mb-3">
            Withdrawal from branch settlement wallet. Platform admins process the bank transfer.
          </p>
          <DetailRow
            label="Reference"
            value={<span className="font-monospace text-break small">{row.payment_reference || "—"}</span>}
          />
          <DetailRow
            label="Amount"
            value={<span className="fw-semibold">{formatMoney(Number(payout).toFixed(2), true)}</span>}
          />
        </div>
      );
    }

    return (
      <div className="py-1">
        <DetailRow
          label="Reference"
          value={<span className="font-monospace text-break small">{row.payment_reference || "—"}</span>}
        />
        <DetailRow label="Type" value={convertToTitleCase(String(row.entry_type || "").replace(/_/g, " "))} />
        <DetailRow label="Amount" value={formatMoney(Number(row.amount_ngn ?? 0).toFixed(2), true)} />
      </div>
    );
  })();

  const withdrawalRequestDetailModal = (() => {
    const row = selectedWithdrawalRequest;
    if (!row) {
      return <p className="text-muted small mb-0">No withdrawal request selected.</p>;
    }
    const staff = row?.requested_by_staff ?? row?.requestedByStaff;
    const bank = row?.company_bank ?? row?.companyBank;
    const statusUpper = String(row?.status || "").toUpperCase();

    return (
      <div className="py-1">
        <p className="small text-muted mb-3">
          Payout request for this branch. Platform administrators approve and send the transfer to
          your linked settlement account.
        </p>
        <DetailRow label="Request ID" value={<span className="fw-semibold">#{row.id}</span>} />
        <DetailRow
          label="Amount"
          value={
            <span className="fw-semibold text-primary1">
              {formatMoney(Number(row?.amount_ngn ?? 0).toFixed(2), true)}
            </span>
          }
        />
        <DetailRow
          label="Status"
          value={
            <span
              className="rounded-pill px-2 py-1 text-white small"
              style={withdrawalStatusStyle(row?.status)}
            >
              {convertToTitleCase(String(row?.status || "—").toLowerCase())}
            </span>
          }
        />
        <DetailRow
          label="Requested by"
          value={
            <span>
              {formatStaffDisplayName(staff)}
              {staff?.email ? (
                <span className="text-muted d-block small mt-1">{staff.email}</span>
              ) : null}
            </span>
          }
        />
        <DetailRow
          label="Submitted"
          value={
            row?.created_at ? moment(row.created_at).format("DD-MMM-YYYY, HH:mm") : "—"
          }
        />
        <DetailRow
          label="Settlement account"
          value={
            bank ? (
              <span>
                <span className="d-block">{bank.bank_name || "—"}</span>
                <span className="d-block small text-muted">
                  {[bank.account_number, bank.account_name].filter(Boolean).join(" · ") || "—"}
                </span>
              </span>
            ) : (
              "—"
            )
          }
        />
        {row.approved_at != null && row.approved_at !== "" && (
          <DetailRow
            label="Approved (processing started)"
            value={moment(row.approved_at).format("DD-MMM-YYYY, HH:mm")}
          />
        )}
        {row.paid_at != null && row.paid_at !== "" && (
          <DetailRow
            label="Paid"
            value={moment(row.paid_at).format("DD-MMM-YYYY, HH:mm")}
          />
        )}
        {row.paystack_transfer_reference != null && row.paystack_transfer_reference !== "" && (
          <DetailRow
            label="Transfer reference"
            value={
              <span className="font-monospace text-break small">{row.paystack_transfer_reference}</span>
            }
          />
        )}
        {(statusUpper === "FAILED" || statusUpper === "REJECTED") && row.failure_reason && (
          <DetailRow
            label={statusUpper === "REJECTED" ? "Rejection reason" : "Failure reason"}
            value={<span className="text-danger small">{row.failure_reason}</span>}
          />
        )}
      </div>
    );
  })();

  const withdrawalModal = (
    <div className="py-2">
      <p className="text-muted small mb-3">
        Request a payout from this branch&apos;s settlement wallet. Uniquecoop admin
        must approve the transfer to your linked bank account.
      </p>
      <p className="small fw-semibold text-black2 mb-3" style={{ color: "var(--color-secondary)" }}>
        Maximum you can request now:{" "}
        {isBalanceLoading ? "…" : formatMoney(Number(availableToWithdrawNgn).toFixed(2), true)}
        {pendingWithdrawalsTotalNgn > 0 && (
          <span className="text-muted fw-normal d-block mt-1 small">
            {formatMoney(Number(pendingWithdrawalsTotalNgn).toFixed(2), true)} is already tied to pending
            withdrawal request(s).
          </span>
        )}
      </p>
      <div className="mb-3">
        <label className="form-label small fw-semibold text-black2">
          Amount (NGN)
        </label>
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className="form-control"
          style={{ backgroundColor: "#fafafa" }}
          value={amountInput}
          onChange={(e) => setAmountInput(formatAmountInputDisplay(e.target.value))}
          placeholder="0.00"
        />
      </div>
      <div className="mb-4">
        <label className="form-label small fw-semibold text-black2">
          Settlement bank account
        </label>
        <select
          className="form-select"
          style={{ backgroundColor: "#fafafa" }}
          value={selectedBankId}
          onChange={(e) => setSelectedBankId(e.target.value)}
          disabled={isBanksLoading}
        >
          <option value="">Select bank account</option>
          {(banks || []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.bank_name} — {b.account_number} ({b.account_name})
              {!b.paystack_recipient_code ? " — recipient not configured" : ""}
            </option>
          ))}
        </select>
        {(banks || []).length === 0 && !isBanksLoading && (
          <p className="text-danger small mt-2 mb-0">
            No active bank accounts for this branch. Add one under Society Bank settings.
          </p>
        )}
      </div>
      <div className="d-flex justify-content-end gap-2">
        <ButtonComp
          btnText="Cancel"
          btnClassName="border border-primary1 text-primary1 px-4 py-2 rounded bg-white"
          onClick={() => setModalPage("")}
        />
        <ButtonComp
          btnText={isSubmitting ? "Submitting..." : "Submit request"}
          btnClassName="bg-primary1 text-white px-4 py-2 rounded border-0"
          onClick={handleSubmitWithdrawal}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );

  return (
    <section className="px-lg-4 min-vh-100 pb-5 mb-3">
      <CardComp cardClassName="border-0 ps-3 mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h3 className="text-black1 fw-bold mb-1">Branch settlement wallet</h3>
            <p className="text-muted mb-0 small">
              Pending settlement balance for your active branch (net of platform fees
              per payment rules). Withdrawals require Uniquecoop approval.
            </p>
          </div>
          <ButtonComp
            btnText="Request withdrawal"
            btnClassName="bg-primary1 text-white px-4 py-2 rounded border-0"
            onClick={() => setModalPage("Request withdrawal")}
            disabled={!isBalanceLoading && Number(availableToWithdrawNgn) <= 0}
            title={
              Number(availableToWithdrawNgn) <= 0 && !isBalanceLoading
                ? "No funds available to withdraw after pending requests, or wallet balance is zero."
                : undefined
            }
          />
        </div>
      </CardComp>

      <div className="row g-3 mb-4">
        <div className="col-md-6 col-lg-3 col-6">
          <CardComp cardClassName="border-0 h-100">
            <p className="text-muted small mb-1">Wallet balance</p>
            <p className="fw-bold text-black1 mb-0" style={{ fontSize: "22px" }}>
              {isBalanceLoading ? "…" : formatMoney(Number(balanceNgn).toFixed(2))}
            </p>
            <p className="text-muted small mb-0 mt-1" style={{ fontSize: "12px" }}>
              Gross balance in this branch wallet
            </p>
          </CardComp>
        </div>
        <div className="col-md-6 col-lg-3 col-6">
          <CardComp cardClassName="border-0 h-100">
            <p className="text-muted small mb-1">Pending withdrawals</p>
            <p className="fw-bold text-black1 mb-0" style={{ fontSize: "22px" }}>
              {isBalanceLoading ? "…" : formatMoney(Number(pendingWithdrawalsTotalNgn).toFixed(2))}
            </p>
            <p className="text-muted small mb-0 mt-1" style={{ fontSize: "12px" }}>
              Awaiting platform approval (reserved)
            </p>
          </CardComp>
        </div>
        <div className="col-md-6 col-lg-3 col-6">
          <CardComp cardClassName="border-0 h-100">
            <p className="text-muted small mb-1">Available to withdraw</p>
            <p className="fw-bold mb-0" style={{ fontSize: "22px", color: "var(--color-secondary)" }}>
              {isBalanceLoading ? "…" : formatMoney(Number(availableToWithdrawNgn).toFixed(2))}
            </p>
            <p className="text-muted small mb-0 mt-1" style={{ fontSize: "12px" }}>
              You can request up to this amount now
            </p>
          </CardComp>
        </div>
        <div className="col-md-6 col-lg-3 col-6">
          <CardComp cardClassName="border-0 h-100">
            <p className="text-muted small mb-1">Pending recovery</p>
            <p className="fw-bold text-black1 mb-0" style={{ fontSize: "22px" }}>
              {isBalanceLoading
                ? "…"
                : formatMoney(Number(pendingRecoveryNgn).toFixed(2))}
            </p>
          </CardComp>
        </div>
      </div>

      <CardComp cardClassName="border-0 shadow-sm">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3 pb-3 border-bottom border-light">
          <button
            type="button"
            className={`px-4 py-2 rounded fw-medium small border-0 ${
              walletTab === WALLET_TABS.LEDGER
                ? "bg-primary1 text-white"
                : "bg-gray3 text-black2"
            }`}
            style={{ minHeight: "40px" }}
            onClick={() => setWalletTab(WALLET_TABS.LEDGER)}
          >
            Ledger history
            {pagination?.total > 0 && (
              <span
                className={`ms-1 rounded-pill px-2 py-0 small ${
                  walletTab === WALLET_TABS.LEDGER ? "bg-white text-primary1" : "bg-white text-muted"
                }`}
              >
                {pagination.total}
              </span>
            )}
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded fw-medium small border-0 ${
              walletTab === WALLET_TABS.WITHDRAWALS
                ? "bg-primary1 text-white"
                : "bg-gray3 text-black2"
            }`}
            style={{ minHeight: "40px" }}
            onClick={() => setWalletTab(WALLET_TABS.WITHDRAWALS)}
          >
            Withdrawal requests
            {wdPagination?.total > 0 && (
              <span
                className={`ms-1 rounded-pill px-2 py-0 small ${
                  walletTab === WALLET_TABS.WITHDRAWALS ? "bg-white text-primary1" : "bg-white text-muted"
                }`}
              >
                {wdPagination.total}
              </span>
            )}
          </button>
        </div>

        {walletTab === WALLET_TABS.WITHDRAWALS && (
          <>
            <p className="text-muted small mb-3">
              Status of payout requests for this branch. New requests cannot exceed &quot;Available to
              withdraw&quot; in the summary above.
            </p>
            <TableCompData
              columns={withdrawalColumns}
              data={withdrawalTableData}
              progressPending={isWdListLoading}
              progressComponent={<TableSkeletonLoader rows={6} columns={6} />}
              message={
                !isWdListLoading && (
                  <EmptyTable Message="No withdrawal requests yet for this branch." />
                )
              }
              cellPadding="12px"
              marginBottom="24px"
            />
            {!isWdListLoading && withdrawalTableData?.length > 0 && (
              <TablePagination
                marginTop="0"
                handlePageChange={setWdPage}
                currentPage={wdPagination.currentPage}
                totalPages={wdPagination.lastPage || wdPagination.totalPages || 1}
                total={wdPagination.total}
                count={withdrawalTableData.length}
                perPage={wdPagination.perPage}
              />
            )}
          </>
        )}

        {walletTab === WALLET_TABS.LEDGER && (
          <>
            <p className="text-muted small mb-3">
              Credits and debits for this branch settlement wallet (payments settled, withdrawals
              paid, etc.).
            </p>
            <TableCompData
              columns={columns}
              data={tableData}
              progressPending={isLedgerLoading}
              progressComponent={<TableSkeletonLoader rows={10} columns={7} />}
              message={
                !isLedgerLoading && (
                  <EmptyTable Message="No wallet entries yet. Entries appear after successful online payments are settled." />
                )
              }
              cellPadding="12px"
              marginBottom="24px"
            />
            {!isLedgerLoading && tableData?.length > 0 && (
              <TablePagination
                marginTop="0"
                handlePageChange={setPage}
                currentPage={pagination.currentPage}
                totalPages={pagination.lastPage || pagination.totalPages || 1}
                total={pagination.total}
                count={tableData.length}
                perPage={pagination.perPage}
              />
            )}
          </>
        )}
      </CardComp>

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={[
          { name: "Request withdrawal", component: withdrawalModal },
          {
            name: "Transaction details",
            component: transactionDetailModal,
          },
          { name: "Withdrawal details", component: withdrawalRequestDetailModal },
        ]}
        handleClose={() => {
          setModalPage("");
          setSelectedEntry(null);
          setSelectedWithdrawalRequest(null);
        }}
        centered
        size={modalPage === "Withdrawal details" ? "lg" : "md"}
        close
      />
    </section>
  );
}
